import Modal from 'bootstrap/js/dist/modal';
import { getModelInfo, populateBrands, populateModel } from './modelLookup';

const prodUrlMap = ((<any>window).prodUrlMap || {}) as Record<string, string>;

const templateHtml = `
  <div class="modal fade" data-bs-keyboard="false" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Does it fit my radio?</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-start">
          <div id="notes">
            <h5>Notes</h5>
            <ul>
              <li>This check is for connector fit only!</li>
              <li>Signal Sticks™ are intended for handheld use; other uses may require adding a counterpoise.</li>
            </ul>
          </div>
          <div class="mb-3 row align-items-center">
            <label for="brand-select" class="col-sm-3 col-form-label">Radio Brand</label>
            <div class="col-sm-9">
              <select class="form-select" id="brand-select">
                <option value="" selected>Select your radio brand</option>
              </select>
            </div>
          </div>
          <div class="mb-3 row align-items-center">
            <label for="model-select" class="col-sm-3 col-form-label">Radio Model</label>
            <div class="col-sm-9">
              <select class="form-select" id="model-select">
                <option value="" selected>Select brand first</option>
              </select>
            </div>
          </div>
          <div id="result" class="alert d-flex d-none align-items-center"></div>
          <div id="strand-warning" class="alert alert-warning d-flex d-none align-items-start"></div>
          <div id="radio-specs" class="d-none mt-3"></div>
        </div>
      </div>
    </div>
  </div>
`;

function reverseConnector(type: string) {
  // SMA-M -> SMA-F, SMA-F -> SMA-M, BNC -> BNC
  if (type.startsWith('SMA-')) {
    return type === 'SMA-M' ? 'SMA-F' : 'SMA-M';
  }
  return type;
}

function buildRadioSpecsHTML(modelInfo: any): string {
  const spacerNumber = isNaN(Number(modelInfo.spacer)) ? 0 : Number(modelInfo.spacer);
  let spacerHTML = '';

  if (modelInfo.spacer === "3d") {
    spacerHTML = `<strong class="text-warning">
      <a href="/products/spacer-3dprinted/" class="text-warning">3D Printed Antenna Spacer</a>
    </strong>`;
  } else if (modelInfo.spacer === "?") {
    spacerHTML = 'Unknown';
  } else if (spacerNumber === 0) {
    spacerHTML = 'No spacer needed';
  } else if (spacerNumber === 1) {
    spacerHTML = `<strong class="text-warning">
      <a href="/products/spacer-rubber/" class="text-warning">1 Rubber Antenna Spacer</a>
    </strong>`;
  } else if (spacerNumber > 1) {
    spacerHTML = `<strong class="text-warning">
      <a href="/products/spacer-rubber/" class="text-warning">${spacerNumber} Rubber Antenna Spacers</a>
    </strong>`;
  }

  let bandsHTML = '';
  if (modelInfo.bands && modelInfo.bands.length > 0) {
    const badges = modelInfo.bands.map((band: string) => `<span class="badge bg-secondary">${band}</span>`).join(' ');
    bandsHTML = `
      <tr>
        <th scope="row">Band Support:</th>
        <td>${badges}</td>
      </tr>`;
  }

  const radioPageUrl = `/radio-models/${modelInfo.slug}`;

  return `
    <div class="card">
      <div class="card-header bg-primary text-white">
        <h2 class="h4 mb-0">Radio Specifications</h2>
      </div>
      <div class="card-body">
        <table class="table table-borderless mb-0">
          <tbody>
            <tr>
              <th scope="row" class="w-50">Brand:</th>
              <td>${modelInfo.brand}</td>
            </tr>
            <tr>
              <th scope="row">Model:</th>
              <td>${modelInfo.model}</td>
            </tr>
            <tr>
              <th scope="row">Antenna Connector:</th>
              <td><strong>${modelInfo.connector}</strong></td>
            </tr>
            <tr>
              <th scope="row">Spacer Recommended:</th>
              <td>${spacerHTML}</td>
            </tr>
            ${bandsHTML}
          </tbody>
        </table>
      </div>
      <div class="card-footer bg-light text-center">
        <a href="${radioPageUrl}" class="btn btn-sm btn-success text-white">
          <i class="fas fa-info-circle me-1"></i>View Full Radio Information
        </a>
      </div>
    </div>
    `;
}


export function display(connector1: string, connector2?: string, productSlug?: string): Promise<void> {
  const template = document.createElement('template');
  template.innerHTML = templateHtml.trim();
  const modalElement = template.content.firstChild as HTMLDivElement;
  const modal = new Modal(modalElement);

  setupListeners(modalElement, modal, connector1, connector2, productSlug);
  populateForm(modalElement);

  modal.show();
  return new Promise((resolve) => {
    modalElement.addEventListener('hidden.bs.modal', () => {
      modalElement.remove();
      resolve();
    });
  });
}

function setupListeners(modalElement: HTMLDivElement, modal: Modal, connector1: string, connector2?: string, productSlug?: string) {
  const brandSelect = modalElement.querySelector('#brand-select') as HTMLSelectElement;
  const modelSelect = modalElement.querySelector('#model-select') as HTMLSelectElement;
  const resultDiv = modalElement.querySelector('#result') as HTMLDivElement;
  const strandWarningDiv = modalElement.querySelector('#strand-warning') as HTMLDivElement;
  const radioSpecsDiv = modalElement.querySelector('#radio-specs') as HTMLDivElement;

  brandSelect.addEventListener('change', () => {
    populateModel($(brandSelect), $(modelSelect));
    resultDiv.classList.add('d-none');
    strandWarningDiv.classList.add('d-none');
    radioSpecsDiv.classList.add('d-none');
  });

  modelSelect.addEventListener('change', () => {
    const modelInfo = getModelInfo(brandSelect.value, modelSelect.value);
    if (!modelInfo) {
      resultDiv.classList.add('d-none');
      strandWarningDiv.classList.add('d-none');
      radioSpecsDiv.classList.add('d-none');
    } else {
      window.plausible?.('compatibility-check', { props: { brand: brandSelect.value, model: modelSelect.value } });

      // Display radio specifications
      radioSpecsDiv.innerHTML = buildRadioSpecsHTML(modelInfo);
      radioSpecsDiv.classList.remove('d-none');

      const cantUseDirectFitAntenna = ['Deep SMA-M, adapter only'].includes(modelInfo.connector) && !connector2;
      const radioConnector = /SMA-M/.test(modelInfo.connector) ? 'SMA-M' : modelInfo.connector;

      // Determine spacer info
      const spacerNumber = isNaN(Number(modelInfo.spacer)) ? 0 : Number(modelInfo.spacer);
      let spacerText = '';

      if (modelInfo.spacer === "3d") {
        spacerText = ', and we recommend adding a <a href="/products/spacer-3dprinted/">3D-printed spacer</a>';
      } else if (modelInfo.spacer === "?") {
        spacerText = ". We don't know if a spacer is recommended";
      } else if (spacerNumber === 0) {
        spacerText = '';
      } else if (spacerNumber === 1) {
        spacerText = ', and we recommend adding a <a href="/products/spacer-rubber/">spacer</a>';
      } else if (spacerNumber > 1) {
        spacerText = `, and we recommend adding <a href="/products/spacer-rubber/">${spacerNumber} spacers</a>`;
      }

      let message = '';
      let className = '';

      // Check if this is an antenna product
      const isAntenna = ['st-sma-f', 'st-sma-m', 'st-bnc'].includes(productSlug || '');
      const isBncAntenna = productSlug === 'st-bnc';

      // If connector 1 is equal to radio connector and connector 2 is BNC-M
      // Or if connector 2 is equal to radio connector and connector 1 is BNC-M
      // Warn the user that while this directly fits the radio a BNC antenna won't fit on this adapter
      // Basically unless they know what they're doing this is not what they want
      let bncAntennaWontFit = false;
      if (
        (connector1 === 'BNC-M' && connector2 === radioConnector && connector2 !== 'BNC')
        || (connector2 === 'BNC-M' && connector1 === radioConnector && connector1 !== 'BNC')
      ) {
        bncAntennaWontFit = true;
      }

      const isDirectFit = (connector1 === 'BNC-M' && radioConnector === 'BNC')
        || (connector2 === 'BNC-M' && radioConnector === 'BNC')
        || (connector1 === radioConnector)
        || (connector2 === radioConnector)
      ;

      if (cantUseDirectFitAntenna || !isDirectFit) {
        const adapterText = isAntenna && ['SMA-M', 'SMA-F', 'BNC'].includes(radioConnector) ? ', but it could with an adapter' : '';
        message = `<i class="fa fa-exclamation-circle fa-xl me-4"></i>
          <div>
            This ${isAntenna ? 'antenna' : 'product'} does not fit your radio directly${adapterText}.
            <br/><br/>
            Visit the <a href="/radio-models/${modelInfo.slug}">radio information page</a> to see all compatible options.
          </div>`;
        className = 'alert-warning';
        strandWarningDiv.classList.add('d-none');
      } else if (bncAntennaWontFit) {
        message = `<i class="fa fa-exclamation-triangle fa-xl me-4"></i>
          <div>
            While this product fits your radio directly, a BNC antenna will not fit on this adapter.
            This adapter is meant to go on a BNC radio to allow fitting an SMA antenna.
            <br/><br/>
            Visit the <a href="/radio-models/${modelInfo.slug}">radio information page</a> to see all compatible options.
          </div>`;
        className = 'alert-warning';
        strandWarningDiv.classList.add('d-none');
      } else {
        message = `<i class="fa fa-check-circle fa-xl me-4"></i>
          <div>
            This ${isAntenna ? 'antenna' : 'product'} fits your radio directly${spacerText}.
            <br/><br/>
            Visit the <a href="/radio-models/${modelInfo.slug}">radio information page</a> to see all compatible options.
          </div>`;
        className = 'alert-success';

        // Show Signal Strand warning for non-BNC antennas
        if (isAntenna && !isBncAntenna) {
          strandWarningDiv.innerHTML = `<i class="fa fa-exclamation-triangle fa-xl me-4"></i>
            <div>
              <strong>This antenna does not work with the Signal Strand™ System.</strong>
              <br/><br/>
              To use the Signal Strand™ counterpoise system with your radio, you would need the BNC antenna with the Signal Strand™ adapter instead.
              Visit the <a href="/radio-models/${modelInfo.slug}">radio information page</a> to see Signal Strand™ compatible options.
            </div>`;
          strandWarningDiv.classList.remove('d-none');
        } else {
          strandWarningDiv.classList.add('d-none');
        }
      }
      resultDiv.className = `alert ${className} d-flex align-items-start text-start`;
      resultDiv.innerHTML = message;
    }
  });
}

function populateForm(modalElement: HTMLDivElement) {
  const brandSelect = modalElement.querySelector('#brand-select') as HTMLSelectElement;
  populateBrands($(brandSelect));
}
